import { ApiError, api } from '@/lib/api';
import type { NoteCreateInput } from '@/lib/types';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * A note written as a SIDE EFFECT of saving a document.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The owner asked for a note box at the bottom of the two create modals, so a
 * remark can be recorded at the moment the document is raised rather than by
 * reopening the record and going to Tasks & notes.
 *
 * The deal form then reused it for a second case (owner, 2026-08-31): the
 * **Next task** field records its content as a note on the deal AND on that
 * deal's company, every time the text changes. Reused rather than copied
 * because everything hard here is the FAILURE half — the specificity ordering
 * in `noteFailureMessage`, the 403 sentence, the never-throws guarantee — and
 * a second copy would agree the day it was written and drift on the first
 * correction. What would drift is a sentence about whether a customer-visible
 * timeline entry exists.
 *
 * ⚠️ **There is no `notes` column on `d_quotes` or `d_invoices`.** This is the
 * `d_notes` activity system — the same rows the Tasks & notes drawer shows — so
 * the note is a SECOND request (`POST /notes`) against a document that has to
 * exist first. A quote and an invoice are both created by a POST that allocates
 * the id, so no arrangement of this UI makes the pair atomic and none should
 * pretend to.
 *
 * ⚠️ **Never claim a rollback that cannot be performed.** There is no
 * transaction across the two writes and no compensating endpoint safe to fire
 * automatically — deleting the quote to "undo" a refused note would destroy the
 * part that worked. So when the document is created and the note is not, the
 * only truthful report is *the document exists, this note did not save*. Saying
 * "nothing was saved" after a successful `POST /quotes` is a lie somebody acts
 * on: they close the modal and raise a second quote. Same rule, and the same
 * reasoning, as the draft LINE items beside it.
 *
 * This module is the arithmetic and the wire; the copy lives with the component
 * in `DocumentFormBits.tsx`, so the two forms cannot word one outcome two ways.
 * It is split off for the mechanical reason the rest of this folder is: a module
 * exporting both components and non-components breaks React Fast Refresh.
 */

/**
 * The server's ceiling on a note body, enforced here too so a 422 is not the
 * first feedback. Matches `NotesPanel`'s `MAX_BODY`; the server trims and
 * requires at least one character.
 */
export const MAX_NOTE_BODY = 20_000;

/**
 * Which document the note hangs off.
 *
 * ⚠️ A discriminated union rather than a partial `NoteCreateInput`, because the
 * body carries a CHECK constraint requiring **exactly one** target id — zero and
 * two are both a 422. Building the input from this makes a second id
 * inexpressible rather than merely unlikely.
 */
export type DraftNoteTarget =
  | { kind: 'quote'; id: number }
  | { kind: 'invoice'; id: number }
  | { kind: 'deal'; id: number }
  | { kind: 'company'; id: number };

/**
 * What the one Save did about the note — and it is deliberately NOT a boolean.
 *
 * A failure has to carry the reason, because the reader is being told about a
 * request they cannot see and are about to decide whether to retype.
 */
export type DraftNoteOutcome = { kind: 'saved' } | { kind: 'failed'; message: string };

/*
 * ⚠️ Exactly ONE id per body, and the switch is exhaustive rather than a chain
 * of ternaries: adding a fifth target type must fail at `tsc` here, not send a
 * body with no target at all — which is the same 422 as sending two.
 */
function createInputFor(target: DraftNoteTarget, body: string): NoteCreateInput {
  switch (target.kind) {
    case 'quote':
      return { body, quoteId: target.id };
    case 'invoice':
      return { body, invoiceId: target.id };
    case 'deal':
      return { body, dealId: target.id };
    case 'company':
      return { body, companyId: target.id };
  }
}

/**
 * Why the note was refused, in a sentence a person can act on.
 *
 * ⚠️ **Ordered by SPECIFICITY, not by which shape arrived first.** `message` on
 * a 422 is only the generic "Validation failed"; the sentence naming what was
 * actually wrong is in `details`, which `fieldErrors` and `formMessage` read.
 * A fallback chain is a claim about the SET of error shapes an endpoint emits,
 * and that set grows without notice — `CompaniesPage` shipped a chain ordered
 * the other way and a better-shaped 422 silently made it worse.
 *
 * `body` is the only key this request can send, so a field error on it is
 * rendered as prose here rather than marked on a control: the note box may not
 * even be on screen by the time this is read.
 */
function noteFailureMessage(cause: unknown): string {
  if (!(cause instanceof ApiError)) {
    return 'The server could not be reached, so the note was not sent.';
  }
  /*
   * A 403 carries NO `details`, so there is nothing per-field to read — and it
   * is a real possibility even when the box was offered: `/notes` is one of the
   * three modules the server genuinely enforces by permission set, and a set
   * can change under an access token that lasts about five days.
   */
  if (cause.status === 403) {
    return 'Your account is not allowed to add notes.';
  }
  return cause.fieldErrors.body ?? cause.formMessage ?? cause.message;
}

/**
 * Write the typed note against a document that now exists.
 *
 * Returns `null` when there is nothing to say — **an empty box sends no request
 * at all**, rather than `body: ''`, which is a 422. `null` is therefore "no note
 * was attempted", which is a different fact from "a note failed" and must not be
 * reported as one.
 *
 * ⚠️ **It never throws.** The caller is inside a create whose document has
 * already been written; letting a rejection escape would land in the form's
 * catch, which reports a FAILED create — telling somebody their quote did not
 * save when it did, after which they raise a second one. Exactly the shape
 * `carryOnToXero` closes on the edit path.
 *
 * ⚠️ **No `AbortSignal`, and that matches the two-phase writes beside it.**
 * `api.createNote` takes none, and neither does `api.createQuoteProduct`: a
 * write that has already left cannot be recalled, so cancelling it on unmount
 * would abandon a request that still lands and leave nobody able to report it.
 */
export async function writeDraftNote(
  target: DraftNoteTarget,
  body: string,
): Promise<DraftNoteOutcome | null> {
  const trimmed = body.trim();
  if (trimmed === '') return null;

  try {
    await api.createNote(createInputFor(target, trimmed));
    return { kind: 'saved' };
  } catch (cause) {
    return { kind: 'failed', message: noteFailureMessage(cause) };
  }
}
